// Context picker — chip row + module/template/org popovers, styled to
// sq_context_picker_mock.html exactly (via sqAi.module.css). Pure presentation
// + popover plumbing; all scope state lives in usePicker.
//
// Templates are PER MODULE: every pinned module that has templates gets its own
// template control + popover, so a comparison can scope each module to its own
// template subset (no single-module restriction, no flat cross-module list).
//
// Org is a GLOBAL cascading multi-select: pick one-or-more nodes at any active tier
// (facility → department → location → subarea, under the tenant's OWN labels); children
// cascade from the selected parents. The selection rides scope.org and narrows EVERY
// pinned module. Tiers + labels + depth come from usePicker (the server's org-levels +
// org-hierarchy endpoints); this file only renders them.

import React, { useEffect, useRef, useState } from "react";
import styles from "../sqAi.module.css";
import { moduleHasTemplates } from "../usePicker";
import { ModuleIcon, LocationIcon, CalendarIcon } from "./icons";

function templateCountLabel(templates) {
  if (!templates.length) return "All templates";
  if (templates.length === 1) return templates[0];
  return `${templates.length} templates`;
}

// Short summary of the sticky date range for its chip ("2025-01-01 → 2026-06-29 · reported").
function dateChipSummary(value) {
  const from = value.from || "…";
  const to = value.to || "…";
  const basis = value.option === "occurrence" ? "occurrence" : "reported";
  return `${from} → ${to} · ${basis}`;
}

// Short summary of a level's selection for its chip ("Winsford" / "2 selected").
function orgLevelSummary(items) {
  if (!items || !items.length) return "";
  if (items.length === 1) return items[0].label || `#${items[0].id}`;
  return `${items.length} selected`;
}

export default function ContextPicker({
  available,
  availableLoading,
  availableError,
  selected,
  templatesFor,
  toggleModule,
  removeModule,
  onModuleTemplatesChange,
  loadTemplates,
  getTemplateEntry,
  org,
  date,
  disabled,
}) {
  // null | "modules" | "org" | "date" | { tpl: <canonical> } — the template panel tracks
  // WHICH module it edits, since multiple modules can each have a template control.
  const [openPanel, setOpenPanel] = useState(null);
  const rootRef = useRef(null);

  const labelOf = (canonical) => {
    const m = available.find((x) => x.canonical === canonical);
    return m ? m.label : canonical;
  };

  // Stable org slices (the bundle object is rebuilt each render; these inner refs aren't).
  const orgLevels = (org && org.levels) || [];
  const orgSelected = (org && org.selected) || {};
  const loadOrgOptions = org && org.loadOptions;
  const getOrgOptions = org && org.getOptions;

  // If the module whose template panel is open is no longer pinned, close it.
  const openTplFor = openPanel && typeof openPanel === "object" ? openPanel.tpl : null;
  useEffect(() => {
    if (openTplFor && !selected.includes(openTplFor)) setOpenPanel(null);
  }, [openTplFor, selected]);

  useEffect(() => {
    if (disabled) setOpenPanel(null);
  }, [disabled]);

  useEffect(() => {
    if (!openPanel) return;
    const onDown = (e) => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpenPanel(null);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpenPanel(null);
    };
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [openPanel]);

  // While the org panel is open, lazily load options for every tier whose parent is
  // already chosen (root is always ready). Idempotent — loadOptions guards on its cache —
  // so re-running as the selection deepens just fetches the newly-ready child tier.
  useEffect(() => {
    if (openPanel !== "org" || !loadOrgOptions || !getOrgOptions) return;
    for (const lvl of orgLevels) {
      if (getOrgOptions(lvl.key).ready) loadOrgOptions(lvl.key);
    }
  }, [openPanel, orgLevels, orgSelected, loadOrgOptions, getOrgOptions]);

  const openModules = () => setOpenPanel((p) => (p === "modules" ? null : "modules"));
  const openOrg = () => setOpenPanel((p) => (p === "org" ? null : "org"));
  const openDate = () => setOpenPanel((p) => (p === "date" ? null : "date"));
  const openTemplates = (canonical) => {
    loadTemplates(canonical);
    setOpenPanel((p) => (p && typeof p === "object" && p.tpl === canonical ? null : { tpl: canonical }));
  };
  const toggleTemplate = (canonical, title) => {
    const current = templatesFor(canonical);
    const next = current.includes(title) ? current.filter((t) => t !== title) : [...current, title];
    onModuleTemplatesChange(canonical, next);
  };

  const tplEntry = openTplFor ? getTemplateEntry(openTplFor) : null;

  // The org adder + summary chips only render when this tenant has ≥1 active tier.
  const hasOrg = orgLevels.length > 0;
  const rootOrgLabel = (orgLevels[0] && orgLevels[0].label) || "location";

  return (
    <div className={styles.panelHost} ref={rootRef}>
      <div className={styles.chiprow}>
        <button
          type="button"
          className={styles.askBtn}
          onClick={openModules}
          disabled={disabled}
          aria-haspopup="true"
          aria-expanded={openPanel === "modules"}
        >
          <span className={styles.at}>＋</span> Ask about…
        </button>

        {selected.map((canonical) => {
          const paired = moduleHasTemplates(canonical);
          const templates = paired ? templatesFor(canonical) : [];
          const chip = (
            <span className={styles.chip}>
              <ModuleIcon canonical={canonical} />
              <span>{labelOf(canonical)}</span>
              <button
                type="button"
                className={styles.chipX}
                onClick={() => removeModule(canonical)}
                aria-label={`Remove ${labelOf(canonical)}`}
              >
                ✕
              </button>
            </span>
          );
          if (!paired) return <React.Fragment key={canonical}>{chip}</React.Fragment>;
          return (
            <span key={canonical} className={styles.pair}>
              {chip}
              <button
                type="button"
                className={`${styles.tplBtn} ${templates.length ? "" : styles.tplBtnEmpty}`}
                onClick={() => openTemplates(canonical)}
                aria-haspopup="true"
                aria-expanded={openTplFor === canonical}
                title={`Choose templates for ${labelOf(canonical)}`}
              >
                <span className={styles.tri}>▸</span>
                <span>{templateCountLabel(templates)}</span>
                <span className={styles.tri}>▾</span>
              </button>
            </span>
          );
        })}

        {/* Org summary chips — one per tier with a selection; ✕ clears it (+ orphaned children). */}
        {hasOrg &&
          orgLevels.map((lvl) => {
            const items = orgSelected[lvl.key] || [];
            if (!items.length) return null;
            return (
              <span className={styles.chip} key={`org-${lvl.key}`}>
                <LocationIcon />
                <span>
                  <span className={styles.orgChipTier}>{lvl.label}:</span> {orgLevelSummary(items)}
                </span>
                <button
                  type="button"
                  className={styles.chipX}
                  onClick={() => org.clearLevel(lvl.key)}
                  aria-label={`Clear ${lvl.label} filter`}
                >
                  ✕
                </button>
              </span>
            );
          })}

        {hasOrg && (
          <button
            type="button"
            className={styles.askBtn}
            onClick={openOrg}
            disabled={disabled}
            aria-haspopup="true"
            aria-expanded={openPanel === "org"}
            title={`Filter by ${rootOrgLabel}`}
          >
            <LocationIcon /> Filter by {rootOrgLabel}
          </button>
        )}

        {/* Sticky date chip — shows the standing range when set; ✕ clears it back to the YTD default. */}
        {date && date.value.set && (
          <span className={styles.chip}>
            <CalendarIcon />
            <span>
              <span className={styles.orgChipTier}>Date:</span> {dateChipSummary(date.value)}
            </span>
            <button
              type="button"
              className={styles.chipX}
              onClick={() => date.clear()}
              aria-label="Clear date range"
            >
              ✕
            </button>
          </span>
        )}

        {date && (
          <button
            type="button"
            className={styles.askBtn}
            onClick={openDate}
            disabled={disabled}
            aria-haspopup="true"
            aria-expanded={openPanel === "date"}
            title="Set a standing date range"
          >
            <CalendarIcon /> {date.value.set ? "Edit dates" : "Date range"}
          </button>
        )}
      </div>

      {openPanel === "modules" && (
        <div className={`${styles.panelPop} ${styles.panelModules}`} role="dialog" aria-label="Add context">
          <h4>Add context</h4>
          <div className={styles.panelScroll}>
            {availableLoading ? (
              <p className={styles.panelMsg}>Loading modules…</p>
            ) : availableError ? (
              <p className={styles.panelMsgError}>{availableError}</p>
            ) : available.length === 0 ? (
              <p className={styles.panelMsg}>No modules available for this tenant.</p>
            ) : (
              available.map((m) => {
                const sel = selected.includes(m.canonical);
                return (
                  <button
                    type="button"
                    key={m.canonical}
                    className={`${styles.opt} ${sel ? styles.optSel : ""}`}
                    onClick={() => toggleModule(m.canonical)}
                    aria-pressed={sel}
                  >
                    <ModuleIcon canonical={m.canonical} />
                    <span className={styles.optLabel}>{m.label}</span>
                    <span className={styles.cb}>✓</span>
                  </button>
                );
              })
            )}
          </div>
          <div className={styles.panelDone}>
            <button type="button" onClick={() => setOpenPanel(null)}>Done</button>
          </div>
        </div>
      )}

      {openPanel === "org" && (
        <div className={`${styles.panelPop} ${styles.panelOrg}`} role="dialog" aria-label="Filter by location">
          <h4>Filter by location</h4>
          {org.levelsLoading ? (
            <p className={styles.panelMsg}>Loading…</p>
          ) : org.levelsError ? (
            <p className={styles.panelMsgError}>{org.levelsError}</p>
          ) : orgLevels.length === 0 ? (
            <p className={styles.panelMsg}>No location hierarchy for this tenant.</p>
          ) : (
            <div className={styles.orgBody}>
              {orgLevels.map((lvl, idx) => {
                const opts = getOrgOptions(lvl.key);
                const sel = orgSelected[lvl.key] || [];
                const parentLabel = idx > 0 ? (orgLevels[idx - 1].label || "parent") : null;
                return (
                  <div className={styles.orgSection} key={lvl.key}>
                    <div className={styles.orgSectionHead}>
                      <span>{lvl.label}</span>
                      {sel.length > 0 && (
                        <button type="button" className={styles.orgClear} onClick={() => org.clearLevel(lvl.key)}>
                          Clear
                        </button>
                      )}
                    </div>
                    <div className={`${styles.panelScroll} ${styles.orgScroll}`}>
                      {!opts.ready ? (
                        <p className={styles.panelMsg}>Select a {parentLabel} first.</p>
                      ) : opts.loading ? (
                        <p className={styles.panelMsg}>Loading…</p>
                      ) : opts.error ? (
                        <p className={styles.panelMsgError}>{opts.error}</p>
                      ) : opts.items.length === 0 ? (
                        <p className={styles.panelMsg}>No options here.</p>
                      ) : (
                        opts.items.map((opt) => {
                          const isSel = sel.some((it) => it.id === opt.id);
                          return (
                            <button
                              type="button"
                              key={opt.id}
                              className={`${styles.opt} ${isSel ? styles.optSel : ""}`}
                              onClick={() => org.toggleOption(lvl.key, opt)}
                              aria-pressed={isSel}
                            >
                              <span className={styles.optLabel}>{opt.label}</span>
                              <span className={styles.cb}>✓</span>
                            </button>
                          );
                        })
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div className={styles.panelDone}>
            <button type="button" onClick={() => setOpenPanel(null)}>Done</button>
          </div>
        </div>
      )}

      {openPanel === "date" && date && (
        <div className={`${styles.panelPop} ${styles.panelDate}`} role="dialog" aria-label="Set a date range">
          <h4>Date range</h4>
          <div className={styles.dateBody}>
            <label className={styles.dateField}>
              <span>From</span>
              <input
                type="date"
                value={date.display.from || ""}
                max={date.display.to || undefined}
                onChange={(e) => date.setFrom(e.target.value)}
              />
            </label>
            <label className={styles.dateField}>
              <span>To</span>
              <input
                type="date"
                value={date.display.to || ""}
                min={date.display.from || undefined}
                onChange={(e) => date.setTo(e.target.value)}
              />
            </label>
            <div className={styles.dateOptionRow}>
              <span className={styles.dateOptionLabel}>Date option</span>
              <div className={styles.dateToggle}>
                <button
                  type="button"
                  className={`${styles.dateToggleBtn} ${date.value.option !== "occurrence" ? styles.dateToggleSel : ""}`}
                  onClick={() => date.setOption("raised")}
                  aria-pressed={date.value.option !== "occurrence"}
                >
                  Raised On
                </button>
                <button
                  type="button"
                  className={`${styles.dateToggleBtn} ${date.value.option === "occurrence" ? styles.dateToggleSel : ""}`}
                  onClick={() => date.setOption("occurrence")}
                  aria-pressed={date.value.option === "occurrence"}
                >
                  Date of Occurrence
                </button>
              </div>
            </div>
            {!date.value.set && (
              <p className={styles.panelMsg}>Showing year-to-date by default. Pick a range to make it your standing filter.</p>
            )}
          </div>
          <div className={styles.panelDone}>
            {date.value.set && (
              <button type="button" className={styles.orgClear} onClick={() => date.clear()}>Clear</button>
            )}
            <button type="button" onClick={() => setOpenPanel(null)}>Done</button>
          </div>
        </div>
      )}

      {openTplFor && (
        <div className={`${styles.panelPop} ${styles.panelTemplates}`} role="dialog" aria-label={`Templates for ${labelOf(openTplFor)}`}>
          <h4>Templates — {labelOf(openTplFor)}</h4>
          <div className={styles.panelScroll}>
            {!tplEntry || tplEntry.loading ? (
              <p className={styles.panelMsg}>Loading templates…</p>
            ) : tplEntry.error ? (
              <p className={styles.panelMsgError}>{tplEntry.error}</p>
            ) : (tplEntry.items || []).length === 0 ? (
              <p className={styles.panelMsg}>No templates — all records will be used.</p>
            ) : (
              (tplEntry.items || []).map((t) => {
                const sel = templatesFor(openTplFor).includes(t.title);
                return (
                  <button
                    type="button"
                    key={t.id}
                    className={`${styles.opt} ${sel ? styles.optSel : ""}`}
                    onClick={() => toggleTemplate(openTplFor, t.title)}
                    aria-pressed={sel}
                  >
                    <span className={styles.optLabel}>{t.title}</span>
                    <span className={styles.cb}>✓</span>
                  </button>
                );
              })
            )}
          </div>
          <div className={styles.panelDone}>
            <button type="button" onClick={() => setOpenPanel(null)}>Done</button>
          </div>
        </div>
      )}
    </div>
  );
}
